import type { EspaceOverview } from "@shared/espace.types";
import { ArrowRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ds/Badge";
import { Button } from "@/components/ds/Button";
import { Card, CardHeader } from "@/components/ds/Card";
import { EmptyState } from "@/components/ds/EmptyState";
import { LinkButton } from "@/components/ds/LinkButton";
import { HabitudesCard } from "./HabitudesCard";
import { REQUEST_STATUS, relativeDate } from "./labels";

/**
 * Le tableau de bord répond à UNE question : qu'est-ce que j'ai à faire ?
 *
 * Il ouvre donc sur ce qui attend l'entreprise, pas sur des compteurs. Les
 * compteurs viennent après, et seulement ceux qui bougent : afficher « 0 entretien »
 * à une entreprise qui n'en a jamais eu ne l'informe de rien.
 */

export function DashboardPage({
	overview,
	onDeposit,
}: {
	overview: EspaceOverview;
	onDeposit: () => void;
}) {
	const waiting = overview.selections
		.map((selection) => ({
			selection,
			pending: selection.profiles.filter((profile) => profile.decision === null),
		}))
		.filter((entry) => entry.pending.length > 0 && !entry.selection.closedAt);

	const toComplete = overview.requests.filter((request) => request.status === "a_completer");
	const published = overview.requests.filter((request) => request.status === "validee");
	const met = overview.selections.flatMap((selection) =>
		selection.profiles.filter((profile) => profile.decision === "oui"),
	);

	const firstName = overview.contact.fullName?.split(" ")[0];

	return (
		<div className="flex flex-col gap-5">
			<header>
				<h1 className="t-h1 text-mg-heading">
					{firstName ? `Bonjour ${firstName}` : "Votre espace"}
				</h1>
				<p className="t-body mt-1 text-mg-secondary">
					{waiting.length > 0 || toComplete.length > 0
						? "Deux ou trois choses vous attendent."
						: "Rien ne vous attend pour le moment."}
				</p>
			</header>

			{toComplete.map((request) => (
				<Card key={request.id} className="border-l-4 border-l-mg-warning">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div className="min-w-0">
							<Badge tone="warning">Complément demandé</Badge>
							<p className="t-h4 mt-2 text-mg-heading">{request.job}</p>
							<p className="t-body mt-1 text-mg-secondary">
								{request.schoolNote ?? REQUEST_STATUS.a_completer?.help}
							</p>
						</div>
						<LinkButton to={`/espace/offres/${request.id}`}>Compléter</LinkButton>
					</div>
				</Card>
			))}

			{waiting.map(({ selection, pending }) => (
				<Card key={selection.id} className="border-l-4 border-l-mg-action">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div className="min-w-0">
							<Badge tone="brand">
								{pending.length === 1
									? "1 profil à regarder"
									: `${pending.length} profils à regarder`}
							</Badge>
							<p className="t-h4 mt-2 text-mg-heading">{selection.title}</p>
							<p className="t-body mt-1 text-mg-secondary">
								{selection.schoolName} · reçu {relativeDate(selection.createdAt)}
							</p>
						</div>
						<LinkButton to={`/espace/profils/${selection.id}`} tone="primary">
							Les découvrir
							<ArrowRight className="size-4" />
						</LinkButton>
					</div>
				</Card>
			))}

			{overview.requests.length === 0 && overview.selections.length === 0 ? (
				<Card padded={false}>
					<EmptyState
						icon={<Users className="size-5" />}
						title="Votre espace est prêt"
						body="Déposez une offre : votre école vous enverra ici les profils qui correspondent, et vous répondrez sans créer de compte."
						action={
							<Button tone="primary" onClick={onDeposit}>
								Déposer une offre
							</Button>
						}
					/>
				</Card>
			) : null}

			<div className="grid gap-4 sm:grid-cols-3">
				<Stat value={overview.requests.length} label="offres déposées" />
				<Stat value={published.length} label="publiées par l'école" />
				<Stat value={met.length} label="profils que vous voulez rencontrer" />
			</div>

			<HabitudesCard overview={overview} />

			{overview.requests.length > 0 ? (
				<Card padded={false}>
					<CardHeader
						title="Vos dernières offres"
						action={
							<Link to="/espace/offres" className="t-label text-mg-action hover:underline">
								Toutes les offres
							</Link>
						}
					/>
					<ul>
						{overview.requests.slice(0, 4).map((request) => {
							const status = REQUEST_STATUS[request.status];
							return (
								<li key={request.id} className="border-b border-mg-border-subtle last:border-b-0">
									<Link
										to={`/espace/offres/${request.id}`}
										className="flex items-center justify-between gap-4 px-5 py-3.5 hover:bg-mg-hover"
									>
										<div className="min-w-0">
											<p className="t-body-md truncate text-mg-heading">{request.job}</p>
											<p className="t-caption mt-0.5 truncate text-mg-secondary">
												{[request.schoolName, request.location, request.contract]
													.filter(Boolean)
													.join(" · ")}
											</p>
										</div>
										<Badge tone={status?.tone ?? "neutral"}>
											{status?.label ?? request.status}
										</Badge>
									</Link>
								</li>
							);
						})}
					</ul>
				</Card>
			) : null}
		</div>
	);
}

function Stat({ value, label }: { value: number; label: string }) {
	return (
		<Card>
			<p className="t-h1 tabular-nums text-mg-heading">{value}</p>
			<p className="t-caption mt-1 text-mg-secondary">{label}</p>
		</Card>
	);
}
